import type { AccountingCategoryWithChildren } from '@cpce/shared';
import { ChevronDownIcon, SearchIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Checkbox } from '@/components/Checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/Popover';
import { buildCategoryNameMap } from '@/utils/accounting-categories.utils';
import { cn } from '@/utils/style';
import { LoadingLabel } from './LoadingLabel';

interface AccountingCategoryMultiSelectProps {
  value: number[];
  onChange: (value: number[]) => void;
  categories: AccountingCategoryWithChildren[];
  className?: string;
  disabled?: boolean;
  // When set, at most one category can be selected: picking a new one replaces the current
  // selection and closes the dropdown.
  single?: boolean;
  isLoading?: boolean;
}

export function AccountingCategoryMultiSelect({
  value,
  onChange,
  categories,
  className,
  disabled,
  single = false,
  isLoading = false,
}: AccountingCategoryMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedIds = useMemo(() => new Set(value), [value]);

  const allCategories = useMemo(() => buildCategoryNameMap(categories), [categories]);

  const topLevelCategories = useMemo(
    () => categories.filter((c) => c.parentId === null),
    [categories]
  );

  const filteredTree = useMemo(() => {
    if (!search) return topLevelCategories;

    const lowerSearch = search.toLowerCase();
    return topLevelCategories
      .map((parent) => {
        const parentMatches = parent.name.toLowerCase().includes(lowerSearch);
        const matchingChildren = parent.children.filter((child) =>
          child.name.toLowerCase().includes(lowerSearch)
        );
        if (parentMatches) return parent;
        if (matchingChildren.length > 0) return { ...parent, children: matchingChildren };
        return null;
      })
      .filter(Boolean) as AccountingCategoryWithChildren[];
  }, [search, topLevelCategories]);

  const toggleCategory = (id: number) => {
    if (single) {
      onChange(selectedIds.has(id) ? [] : [id]);
      setOpen(false);
      return;
    }
    if (selectedIds.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const displayValue = useMemo(() => {
    if (value.length === 0) return null;
    const firstName = allCategories.get(value[0]) ?? '';
    return { displayName: firstName, remaining: value.length - 1 };
  }, [value, allCategories]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-8 w-full items-center justify-between gap-2 rounded-md border border-gray-300 bg-transparent px-3 py-1.5 text-sm whitespace-nowrap transition-colors outline-none focus:border-primary focus:ring-1 focus:ring-primary',
            open && 'border-primary ring-1 ring-primary',
            disabled && 'cursor-not-allowed opacity-50',
            className
          )}
        >
          <span className="flex items-center gap-1.5 overflow-hidden">
            {displayValue ? (
              <>
                <span className="truncate">{displayValue.displayName}</span>
                {displayValue.remaining > 0 && (
                  <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
                    +{displayValue.remaining}
                  </span>
                )}
              </>
            ) : (
              <span className="text-gray-400">
                {single ? 'Catégorie comptable...' : 'Catégories comptables...'}
              </span>
            )}
          </span>
          <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2 border-b border-gray-200 px-3 py-2">
          <SearchIcon className="size-4 shrink-0 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher une catégorie..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-2">
          {filteredTree.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">
              {isLoading ? <LoadingLabel /> : 'Aucun résultat'}
            </p>
          ) : (
            filteredTree.map((parent) => (
              <div key={parent.id}>
                <CategoryRow
                  id={parent.id}
                  name={parent.name}
                  checked={selectedIds.has(parent.id)}
                  onToggle={toggleCategory}
                />
                {parent.children.map((child) => (
                  <CategoryRow
                    key={child.id}
                    id={child.id}
                    name={child.name}
                    checked={selectedIds.has(child.id)}
                    onToggle={toggleCategory}
                    indent
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CategoryRow({
  id,
  name,
  checked,
  onToggle,
  indent,
}: {
  id: number;
  name: string;
  checked: boolean;
  onToggle: (id: number) => void;
  indent?: boolean;
}) {
  return (
    <div
      onClick={() => onToggle(id)}
      onKeyDown={(e) => e.key === 'Enter' && onToggle(id)}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-gray-50',
        indent && 'pl-6'
      )}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={() => onToggle(id)}
        onClick={(e) => e.stopPropagation()}
      />
      <span className={cn('text-sm', !indent && 'font-medium')}>{name}</span>
    </div>
  );
}
